<template>
    <div class="nk-block-head nk-block-head-sm sip-page-intro">
        <div class="nk-block-between sip-page-intro__row">
            <div class="nk-block-head-content sip-page-intro__copy">
                <nav
                    v-if="hasMeta"
                    class="sip-page-intro__meta"
                    aria-label="Breadcrumb"
                >
                    <span
                        v-if="hasBadge"
                        class="sip-page-intro__badge"
                        :class="'sip-page-intro__badge--' + badgeTone"
                    >
                        <span
                            v-if="livePulse"
                            class="sip-page-intro__pulse"
                            aria-hidden="true"
                        >
                            <span class="sip-page-intro__pulse-ping"></span>
                            <span class="sip-page-intro__pulse-dot"></span>
                        </span>
                        <span>{{ badgeText }}</span>
                    </span>

                    <span
                        v-if="hasBadge && crumbs.length"
                        class="sip-page-intro__meta-sep"
                        aria-hidden="true"
                    >·</span>

                    <ol v-if="crumbs.length" class="sip-page-intro__crumbs">
                        <template v-for="(crumb, index) in crumbs">
                            <li
                                v-if="index > 0"
                                :key="'sep-' + index"
                                class="sip-page-intro__crumb-sep"
                                aria-hidden="true"
                            >/</li>
                            <li :key="'crumb-' + index">
                                <router-link
                                    v-if="crumb.to && index < crumbs.length - 1"
                                    :to="crumb.to"
                                    class="sip-page-intro__crumb-link"
                                >{{ crumb.label }}</router-link>
                                <a
                                    v-else-if="crumb.href && index < crumbs.length - 1"
                                    :href="crumb.href"
                                    class="sip-page-intro__crumb-link"
                                >{{ crumb.label }}</a>
                                <span
                                    v-else
                                    class="sip-page-intro__crumb-current"
                                >{{ crumb.label }}</span>
                            </li>
                        </template>
                    </ol>
                </nav>

                <h3 v-if="hasTitle" class="nk-block-title page-title sip-page-intro__title">{{ title }}</h3>

                <p
                    v-if="hasSubtitle"
                    class="sip-page-intro__subtitle"
                >{{ subtitle }}</p>

                <div
                    v-if="hasDescription"
                    class="nk-block-des text-soft sip-page-intro__des"
                >
                    <slot name="description">
                        <p v-if="description">{{ description }}</p>
                    </slot>
                </div>
            </div>

            <div
                v-if="hasActions"
                class="nk-block-head-content sip-page-intro__actions"
            >
                <slot name="actions">
                    <slot></slot>
                </slot>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: 'SipPageIntro',
    props: {
        title: {
            type: String,
            required: true
        },
        subtitle: {
            type: String,
            default: null
        },
        description: {
            type: String,
            default: null
        },
        badge: {
            type: String,
            default: null
        },
        badgeTone: {
            type: String,
            default: 'cyan',
            validator: (v) => ['cyan', 'emerald', 'rose', 'amber', 'indigo', 'slate'].indexOf(v) !== -1
        },
        breadcrumbs: {
            type: Array,
            default: () => []
        },
        livePulse: {
            type: Boolean,
            default: false
        }
    },
    computed: {
        hasTitle() {
            return typeof this.title === 'string' && this.title.trim().length > 0;
        },
        badgeText() {
            return typeof this.badge === 'string' ? this.badge.trim() : '';
        },
        hasBadge() {
            return this.badgeText.length > 0;
        },
        hasSubtitle() {
            return typeof this.subtitle === 'string' && this.subtitle.trim().length > 0;
        },
        crumbs() {
            if (!this.breadcrumbs || !Array.isArray(this.breadcrumbs)) return [];
            return this.breadcrumbs.map((crumb) => {
                if (!crumb) return null;
                if (typeof crumb === 'string') {
                    const label = crumb.trim();
                    return label ? { label } : null;
                }
                const label = (crumb.label || crumb.title || '').trim();
                return label ? {
                    label,
                    to: crumb.to || null,
                    href: crumb.href || crumb.url || null
                } : null;
            }).filter(Boolean);
        },
        hasMeta() {
            return this.hasBadge || this.crumbs.length > 0;
        },
        hasDescription() {
            if (typeof this.description === 'string' && this.description.trim().length > 0) {
                return true;
            }
            if (this.$slots.description && this.$slots.description.length > 0) {
                return this.hasVNodeContent(this.$slots.description);
            }
            return false;
        },
        hasActions() {
            return this.hasVNodeContent(this.$slots.actions) || this.hasVNodeContent(this.$slots.default);
        }
    },
    methods: {
        hasVNodeContent(nodes) {
            if (!nodes || !nodes.length) return false;
            return nodes.some((vnode) => {
                if (!vnode) return false;
                if (vnode.tag) return true;
                if (typeof vnode.text === 'string' && vnode.text.trim().length > 0) return true;
                if (vnode.children && vnode.children.length) {
                    return this.hasVNodeContent(vnode.children);
                }
                return false;
            });
        }
    }
};
</script>
